/*
 * Mizz Rio theme behaviour. Each part initialises inside a root element, so the
 * theme editor can re-run it on a section it has just re-rendered.
 */
;(() => {
  const reduced = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

  /* ---- header: bone background once scrolled, and the phone menu ---- */

  function initHeader(root) {
    const header = root.querySelector('[data-header]')
    const menu = root.querySelector('[data-menu]')
    const toggle = root.querySelector('[data-menu-toggle]')
    if (!header) return
    const onScroll = () => header.classList.toggle('site-header--scrolled', window.scrollY > 8)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    if (!menu || !toggle) return
    const label = toggle.querySelector('[data-menu-label]')
    const setOpen = (open) => {
      header.classList.toggle('site-header--open', open)
      menu.classList.toggle('site-menu--open', open)
      menu.setAttribute('aria-hidden', String(!open))
      toggle.setAttribute('aria-expanded', String(open))
      if (label) label.textContent = open ? 'Close menu' : 'Open menu'
      document.documentElement.style.overflow = open ? 'hidden' : ''
    }
    toggle.addEventListener('click', () => setOpen(!header.classList.contains('site-header--open')))
    menu.addEventListener('click', (e) => {
      if (e.target.closest('a')) setOpen(false)
    })
  }

  /* ---- reveal: fades content up once it enters the viewport ---- */

  function initReveal(root) {
    const els = root.querySelectorAll('.reveal:not(.is-in)')
    if (!els.length) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in')
            io.unobserve(entry.target)
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 },
    )
    els.forEach((el) => io.observe(el))
  }

  /* ---- parallax: drifts content vertically against the scroll, within a clipped frame ---- */

  const parallaxFrames = new Set()
  let parallaxRaf = 0

  function updateParallax() {
    parallaxRaf = 0
    const vh = window.innerHeight
    for (const frame of parallaxFrames) {
      if (!frame.isConnected) {
        parallaxFrames.delete(frame)
        continue
      }
      const inner = frame.firstElementChild
      const speed = parseFloat(frame.dataset.parallax) || 0.14
      const rect = frame.getBoundingClientRect()
      if (rect.bottom < 0 || rect.top > vh) continue
      // progress runs -1 (just below the viewport) to 1 (just above)
      const progress = (rect.top + rect.height / 2 - vh / 2) / (vh / 2 + rect.height / 2)
      inner.style.transform = `translate3d(0, ${(-progress * speed * rect.height).toFixed(1)}px, 0)`
    }
  }

  function requestParallax() {
    if (!parallaxRaf) parallaxRaf = requestAnimationFrame(updateParallax)
  }

  function initParallax(root) {
    if (reduced()) return
    root.querySelectorAll('[data-parallax]').forEach((frame) => parallaxFrames.add(frame))
    updateParallax()
  }

  window.addEventListener('scroll', requestParallax, { passive: true })
  window.addEventListener('resize', requestParallax)

  /* ---- hero ---- */

  /*
   * The photograph's left edge is measured from the type: it falls in the middle of
   * the space between "is" and "now.", so the whole word crosses into the picture
   * at every width and neither word touches the edge.
   */
  function initWordEdge(section) {
    const prev = section.querySelector('[data-hero-before]')
    const word = section.querySelector('[data-hero-word]')
    if (!prev || !word) return
    const measure = () => {
      // measured against the section, not the centred container inside it
      const x0 = section.getBoundingClientRect().left
      const gapStart = prev.getBoundingClientRect().right - x0
      const gapEnd = word.getBoundingClientRect().left - x0
      section.style.setProperty('--m-left', `${Math.round((gapStart + gapEnd) / 2)}px`)
    }
    measure()
    if (document.fonts) document.fonts.ready.then(measure)
    new ResizeObserver(measure).observe(section)
  }

  /*
   * The hero's entrance waits for its photograph: "now." is set in bone over the
   * picture, so it would vanish against an empty frame. The animations stay paused
   * until the image has decoded, and never wait longer than the cap.
   */
  const HERO_WAIT_CAP = 1200

  function initHeroReady(section) {
    const img = section.querySelector('img')
    let done = false
    const ready = () => {
      if (done) return
      done = true
      section.setAttribute('data-ready', '')
    }
    if (!img || (img.complete && img.naturalWidth > 0)) return ready()
    img.decode().then(ready, ready)
    window.setTimeout(ready, HERO_WAIT_CAP)
  }

  function initHero(root) {
    root.querySelectorAll('[data-hero]').forEach((section) => {
      initWordEdge(section)
      initHeroReady(section)
    })
  }

  /* ---- countdown: the mark travels down the ruler and the months tick down with it ---- */

  function initCountdown(root) {
    root.querySelectorAll('[data-countdown]').forEach((ol) => {
      const section = ol.closest('section')
      const counter = section && section.querySelector('[data-countdown-counter]')
      const num = counter && counter.querySelector('[data-num]')
      const label = counter && counter.querySelector('[data-label]')
      const stages = Array.from(ol.querySelectorAll('.countdown__stage'))
      if (!stages.length) return
      if (reduced()) {
        ol.classList.add('countdown--static')
        return
      }
      const at = stages.map((li) => Number(li.dataset.months) || 0)
      let shown = null
      const show = (months) => {
        if (months === shown || !num) return
        shown = months
        num.textContent = months
        label.textContent = months === 0 ? 'The day' : months === 1 ? 'month to the day' : 'months to the day'
      }
      let raf = 0
      const update = () => {
        raf = 0
        const top = ol.getBoundingClientRect().top
        // the reading line: a little above the middle of the viewport
        const line = window.innerHeight * 0.42 - top
        // each stage's tick is its ::before rule; read its resolved position in px
        const ticks = stages.map((li) => li.offsetTop + parseFloat(getComputedStyle(li, '::before').top))
        const first = ticks[0]
        const last = ticks[ticks.length - 1]
        const p = Math.min(Math.max(line, first), last)
        ol.style.setProperty('--mark', `${p}px`)
        let months = at[0]
        for (let i = 0; i < stages.length; i++) {
          stages[i].classList.toggle('is-past', ticks[i] <= p + 1)
          if (i < stages.length - 1 && ticks[i] <= p && p <= ticks[i + 1]) {
            const f = (p - ticks[i]) / (ticks[i + 1] - ticks[i])
            months = at[i] + (at[i + 1] - at[i]) * f
          }
        }
        if (p >= last) months = at[at.length - 1]
        show(Math.round(months))
      }
      const onScroll = () => {
        if (!raf) raf = requestAnimationFrame(update)
      }
      update()
      window.addEventListener('scroll', onScroll, { passive: true })
      window.addEventListener('resize', onScroll)
    })
  }

  function init(root) {
    initHeader(root)
    initHero(root)
    initReveal(root)
    initParallax(root)
    initCountdown(root)
  }

  init(document)

  // the theme editor re-renders a section in place when its settings change
  document.addEventListener('shopify:section:load', (e) => init(e.target))
})()
